import { LogOut, House, ChevronDown, ChevronUp, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";

import { useAuth } from '../contexts/AuthContext';
import { ROUTES_CONFIG } from "../config/routes-permissions";

const SubMenu = ({ items, parentPath, currentPath, toggleSubMenu, isExpanded }) => {
  if (!items || items.length === 0) return null;

  return (
    <div className={`transition-all duration-300 ease-in-out overflow-hidden
      ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
      <div className="pl-4">
        {items.map((item, index) => (
          <Link
            key={`${parentPath}${item.path}`}
            to={`${parentPath}${item.path}`}
            className={`block py-2 text-base font-medium transition-all duration-300 mb-[1.375rem]
              ${currentPath === `${parentPath}${item.path}`
                ? ' text-black bg-stone-200 pl-3 rounded-md'
                : 'text-gray-600 hover:bg-stone-200 pl-3 rounded-md'
              }`}
          >
            {item.label}
          </Link>
        ))}
      </div>
    </div>
  );
};

const NavItem = ({ item, currentPath, hasPermission, expandedItems, toggleItemExpansion }) => {
  // 過濾有權限的子項目
  const authorizedSubItems = item.subItems 
    ? item.subItems.filter(subItem => hasPermission(`${item.path}${subItem.path}`))
    : [];
  
  const hasSubMenu = authorizedSubItems.length > 0;
  
  // 檢查是否有子選單項目被選中
  const isSubItemActive = hasSubMenu && authorizedSubItems.some(
    subItem => currentPath === `${item.path}${subItem.path}`
  );
  
  // 檢查當前項目是否被選中（包括子選單）
  const isActive = currentPath === item.path || isSubItemActive;
  const isExpanded = expandedItems.includes(item.path);
  
  // 切換子選單顯示狀態
  const toggleSubMenu = (e) => {
    if (hasSubMenu) {
      e.preventDefault();
      toggleItemExpansion(item.path);
    }
  };

  // 如果當前項目沒有權限或沒有授權的子項目，不渲染
  if (!hasPermission(item.path) || (item.subItems && item.subItems.length > 0 && authorizedSubItems.length === 0)) {
    return null;
  }

  return (
    <div className="">
      <Link
        to={hasSubMenu ? "#" : item.path}
        className={`px-4 py-1.5 rounded-md flex items-center justify-between text-base font-medium text-black transition-colors mb-[1.375rem]
          ${isActive
            ? 'bg-lightGray'
            : 'hover:bg-stone-200'
          }`}
        onClick={toggleSubMenu}
      >
        <span>{item.label}</span>
        {hasSubMenu && (
          <span className={`ml-2 transition-transform duration-300 ease-in-out
            ${isExpanded ? 'transform rotate-180' : 'transform rotate-0'}`}>
            <ChevronDown size={20} />
          </span>
        )}
      </Link>
      {hasSubMenu && (
        <SubMenu 
          items={authorizedSubItems}
          parentPath={item.path}
          currentPath={currentPath}
          toggleSubMenu={toggleSubMenu}
          isExpanded={isExpanded}
        />
      )}
    </div>
  );
};

const Sidebar = function () {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState([]);
  const sidebarRef = useRef(null);

  // 導覽列設定
  const navItems = [
    // { path: '/profile', label: '管理員帳戶' },
    { path: '/', label: 'Homepage' },
    { path: '/admin', label: 'Access Control' },
    { 
      path: '/member', 
      label: 'Member Management',
      subItems: [
        { path: '/account', label: 'Member' },
        { path: '/subscription', label: 'Order' },
        { path: '/activity', label: 'Activity' },
      ]
    },
    { path: '/company/profile', label: 'Company Management' },
    { path: '/event', label: 'Event Management' },
    { path: '/promotion/offer', label: 'Promotion Management' },
  ];

  // 檢查用戶是否有權限訪問特定路由
  const hasPermission = (path) => {
    const routeConfig = ROUTES_CONFIG[path];
    if (!routeConfig) return false;
    
    return routeConfig.roles.some(role => user.roles.includes(role));
  };

  // 切換子項目展開狀態
  const toggleItemExpansion = (itemPath) => {
    setExpandedItems(prev => 
      prev.includes(itemPath)
        ? prev.filter(path => path !== itemPath)
        : [...prev, itemPath]
    );
  };

  // 檢查側邊欄中是否有子項目被選中，如果有，初始展開該父項目
  useEffect(() => {
    const newExpandedItems = [];
    
    navItems.forEach(item => {
      if (item.subItems) {
        const hasActiveSubItem = item.subItems.some(
          subItem => location.pathname === `${item.path}${subItem.path}`
        );
        
        if (hasActiveSubItem && !expandedItems.includes(item.path)) {
          newExpandedItems.push(item.path);
        }
      }
    });
    
    if (newExpandedItems.length > 0) {
      setExpandedItems(prev => [...prev, ...newExpandedItems]);
    }
  }, [location.pathname]);

  return (
    <>
      {/* 側邊欄主體 - 在收合時完全隱藏 */}
      <div 
        ref={sidebarRef}
        className={`fixed top-[calc(66px+1.25rem)] left-0 h-full bg-lightGray z-10 transition-all duration-300 flex flex-col w-72`}
      >        
        {/* 導航菜單 */}
        <div className="flex-1 py-[2.25rem] px-[1.875rem] overflow-y-auto">
          {navItems.map(item => (
            <NavItem 
              key={item.path}
              item={item}
              currentPath={location.pathname}
              hasPermission={hasPermission}
              expandedItems={expandedItems}
              toggleItemExpansion={toggleItemExpansion}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default Sidebar;